/**
 * IntentPickerPage — appears immediately after signup. Multi-select cards:
 * "Hire vendors" · "Become a vendor" · "Make a website". Pick 1, 2, or all 3.
 * POSTs to /api/auth/set-intent which upgrades roles + returns next path.
 */
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ShoppingBag, Store, Globe, Check, ArrowRight, Loader2, Sparkles } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import {
  GlassCard, GradientIconTile, DashboardBackdrop,
} from '@/components/dashboard/dashboard-ui';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OPTIONS = [
  {
    id: 'hire',
    icon: ShoppingBag,
    ramp: 'cyan',
    title: 'Hire vendors',
    tagline: 'Shop · book · commission',
    desc: 'Browse Indigenous-owned vendors and order products or services.',
  },
  {
    id: 'vendor',
    icon: Store,
    ramp: 'violet',
    title: 'Become a vendor',
    tagline: 'Sell on the marketplace',
    desc: 'List products and services to thousands of buyers who actively support Indigenous business.',
  },
  {
    id: 'builder',
    icon: Globe,
    ramp: 'fuchsia',
    title: 'Make a website',
    tagline: 'Drag-and-drop builder',
    desc: 'Spin up a beautiful website in minutes — pick a template, edit, publish.',
  },
];

export default function IntentPickerPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, getAuthHeaders, refreshUser } = useAuth();
  const [picked, setPicked] = useState({ hire: false, vendor: false, builder: false });
  const [saving, setSaving] = useState(false);

  // Pre-check based on ?intent= query string (from the homepage hero buttons).
  useEffect(() => {
    const hint = searchParams.get('intent');
    if (hint === 'vendor')  setPicked((p) => ({ ...p, vendor:  true }));
    if (hint === 'hire')    setPicked((p) => ({ ...p, hire:    true }));
    if (hint === 'builder') setPicked((p) => ({ ...p, builder: true }));
  }, [searchParams]);

  // If the user already picked their intent, bounce them to wherever they belong.
  useEffect(() => {
    if (!user) return;
    if (user.intent_chosen === true) {
      const p = (user.roles || []).includes('vendor') ? '/dashboard/vendor'
              : (user.roles || []).includes('website_builder') ? '/website-builder'
              : '/browse';
      navigate(p, { replace: true });
    }
  }, [user, navigate]);

  const toggle = (id) => setPicked((p) => ({ ...p, [id]: !p[id] }));

  const continueOn = async () => {
    if (!picked.hire && !picked.vendor && !picked.builder) {
      toast.error('Pick at least one option to continue');
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/auth/set-intent`, picked, { headers: getAuthHeaders() });
      // Refresh user so route guards see the new roles
      if (refreshUser) {
        try { await refreshUser(); } catch {}
      }
      toast.success("You're all set!");
      // If they picked BOTH vendor + builder, surface a friendly nudge that
      // the Website Builder tab waits for them inside the vendor dashboard.
      if (picked.vendor && picked.builder) {
        try { localStorage.setItem('show_builder_tab_nudge', '1'); } catch {}
      }
      navigate(r.data?.next_path || '/browse', { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save — try again');
    } finally {
      setSaving(false);
    }
  };

  const count = (picked.hire ? 1 : 0) + (picked.vendor ? 1 : 0) + (picked.builder ? 1 : 0);

  return (
    <div className="min-h-screen bg-[#06061A] text-white">
      <main className="relative max-w-5xl mx-auto px-6 py-16" data-testid="intent-picker">
        <DashboardBackdrop />
        <div className="relative z-10">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-violet-500/12 border border-violet-400/30 text-violet-200 text-[11px] font-bold tracking-[0.18em] uppercase mb-5">
              <Sparkles size={12} /> Welcome to EntrepreNatives
            </div>
            <h1 className="text-3xl sm:text-5xl font-bold font-syne tracking-tight" data-testid="intent-title">
              How do you want to use it?
            </h1>
            <p className="text-white/55 mt-3 text-sm sm:text-base max-w-xl mx-auto">
              Pick one — or all three. You can change this anytime from your profile.
            </p>
          </div>

          {/* Option cards */}
          <div className="grid sm:grid-cols-3 gap-5 mb-10">
            {OPTIONS.map((opt) => {
              const active = picked[opt.id];
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => toggle(opt.id)}
                  className={`relative text-left transition-all rounded-2xl border p-6 group ${
                    active
                      ? 'border-[#A78BFA] bg-gradient-to-br from-violet-600/15 to-fuchsia-600/10 shadow-[0_0_30px_rgba(167,139,250,0.25)]'
                      : 'border-white/[0.08] bg-white/[0.025] hover:border-white/30 hover:bg-white/[0.05]'
                  }`}
                  data-testid={`intent-${opt.id}`}
                >
                  {active && (
                    <span className="absolute top-4 right-4 w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #8B5CF6, #EC4899)', boxShadow: '0 6px 18px -4px rgba(139,92,246,0.6)' }}>
                      <Check size={13} className="text-white" strokeWidth={3} />
                    </span>
                  )}
                  <GradientIconTile icon={opt.icon} ramp={opt.ramp} size="lg" />
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mt-4 mb-1">{opt.tagline}</p>
                  <h3 className="text-xl font-bold font-syne">{opt.title}</h3>
                  <p className="text-sm text-white/65 mt-2 leading-relaxed">{opt.desc}</p>
                </button>
              );
            })}
          </div>

          {/* Footer / Continue */}
          <div className="flex flex-col items-center gap-3">
            <button
              onClick={continueOn}
              disabled={saving || count === 0}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-base font-bold uppercase tracking-wider shadow-[0_18px_46px_rgba(139,92,246,0.4)] hover:shadow-[0_18px_60px_rgba(139,92,246,0.55)] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              data-testid="intent-continue"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <>Continue {count > 0 && <span className="opacity-75">· {count} selected</span>}</>}
              {!saving && <ArrowRight size={16} />}
            </button>
            <p className="text-[11px] text-white/45 max-w-md text-center">
              You can pick more than one — vendors can sell AND build a website. We'll route you to the right starting point.
            </p>
          </div>

          {/* "Both" callout when 2+ picked */}
          {count > 1 && (
            <GlassCard glow className="mt-10 p-5 flex items-center gap-4 max-w-2xl mx-auto" data-testid="intent-both-callout">
              <GradientIconTile icon={Sparkles} ramp="lime" size="sm" />
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-[0.18em] text-emerald-300 font-bold mb-0.5">Smart path</p>
                <p className="text-sm text-white">
                  {picked.vendor && picked.builder
                    ? "We'll start you in the vendor dashboard — your Website Builder tab is one click away."
                    : picked.vendor && picked.hire
                    ? "You'll land in your vendor dashboard. Shopping the marketplace is always one click away from the nav."
                    : "We'll route you to the most relevant starting point."}
                </p>
              </div>
            </GlassCard>
          )}
        </div>
      </main>
    </div>
  );
}
